import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import SEO from '../../components/SEO'
import FAQSection from '../../components/FAQSection'

export default function SunsetCruise() {
    const [isModalOpen, setIsModalOpen] = useState(false)

    const faqs = [
        { question: 'What drinks are included in the "open bar"?', answer: 'Most operators provide unlimited local beers (like Nile Special and Bell), house wines, sodas, and mineral water. Some premium cruises may include local spirits (gin, vodka, waragi) and mixers. Premium international spirits are usually not included but may be available for purchase.' },
        { question: 'Is the cruise safe for children?', answer: 'Absolutely. Sunset cruises are very family-friendly. The water is calm, the boats are stable with high railings, and life jackets are provided for all passengers, with specific sizes for children. It\'s a relaxing experience suitable for all ages.' },
        { question: 'Do we get off the boat at the Source of the Nile?', answer: 'Generally, no. The boat will navigate to the specific point where the lake flows into the river, allowing you to view the bubbling water and landmarks from the deck. You get excellent views of the Speke Monument and Gandhi statue from the water.' },
        { question: 'What happens if it rains?', answer: 'Cruises typically run rain or shine. Most boats have a covered lower deck or canopy to protect you from rain while still allowing you to enjoy the river. In the event of severe storms, the operator may delay departure or reschedule for safety.' },
        { question: 'Can I book a private cruise?', answer: 'Yes! Private charters are available for couples, families, or groups. This is a popular option for proposals, birthdays, or corporate retreats. Private cruises can be customized with specific food, music, and extended durations. Contact us for a quote.' },
        { question: 'Is there a toilet on board?', answer: 'Most larger cruise boats (double-deckers) are equipped with a basic marine toilet. Smaller pontoon boats used for more intimate groups may not have facilities on board, so it is recommended to use the restrooms at the dock before departure.' }
    ];

    return (
        <div>
            <SEO
                title="Jinja Sunset Cruise – Source of the Nile Boat Tour | 2024/2025"
                description="Experience the magic of the Nile at twilight. 2-hour sunset boat cruise to the Source of the Nile with complimentary drinks, snacks, and wildlife viewing. The perfect way to end your day in Jinja."
            />
            {/* Hero Section */}
            <section
                className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
                style={{
                    backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600)',
                }}
            >
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl text-white">
                        <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
                            Jinja Sunset Cruise
                        </h1>
                        <p className="text-xl md:text-2xl mb-6">
                            Witness the golden hour over the Source of the Nile with complimentary drinks and stunning views
                        </p>
                        <div className="flex items-center gap-6 mb-6">
                            <div className="flex items-center gap-2">
                                <div className="flex text-safari-gold">
                                    {[...Array(5)].map((_, i) => (
                                        <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                                    ))}
                                </div>
                                <span className="text-lg">(200+ reviews)</span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-4 mb-8">
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-glass-cheers" className="w-4 h-4 mr-2" />
                                Unlimited Drinks
                            </div>
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-camera" className="w-4 h-4 mr-2" />
                                Golden Hour
                            </div>
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-water" className="w-4 h-4 mr-2" />
                                Source of the Nile
                            </div>
                        </div>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                            Book Your Cruise
                        </button>
                    </div>
                </div>
            </section>

            {/* Page Content */}
            <div className="container mx-auto px-4 py-16">
                <div className="lg:grid lg:grid-cols-3 lg:gap-12">
                    {/* Main Content */}
                    <main className="lg:col-span-2">
                        {/* Description Section */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                                The Perfect End to Your Jinja Adventure
                            </h2>
                            <div className="prose prose-lg max-w-none">
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    As the African sun begins its descent, painting the sky in brilliant hues of orange, pink, and purple, there is no better place to be than floating on the calm waters of the Nile River. The Jinja Sunset Cruise is more than just a boat ride; it's a magical 2-hour journey that combines relaxation, history, and natural beauty at the very spot where the world's longest river begins its 6,650-kilometer journey to the Mediterranean.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    Your evening begins as you board a comfortable, double-decker boat or spacious pontoon, greeted by a friendly crew and a cold drink. As you cast off from the riverbank, the day's heat gives way to a cool evening breeze. You'll cruise past lush islands and riverbanks teeming with birdlife—keep your eyes peeled for African fish eagles swooping for their dinner, pied kingfishers hovering over the water, and egrets wading in the shallows. You might even spot otters playing near the rocks or monitor lizards basking in the fading light.
                                </p>

                                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">History & Heritage on the Water</h3>
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    This cruise isn't just about the views; it's a journey through history. Your captain will navigate you to the actual Source of the Nile, a geographically significant point that captivated explorers for centuries. You'll pass the John Hanning Speke Monument, commemorating the British explorer who first documented the source in 1858, and see the Mahatma Gandhi statue, marking the spot where a portion of his ashes were scattered in the Nile, honoring his wish to be one with the world's great rivers.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    As you soak in the history and scenery, enjoy an open bar featuring ice-cold beers (like the local favorite, Nile Special), wine, sodas, and water, accompanied by freshly prepared snacks like BBQ skewers, samosas, and fruit. Whether you're toasting to a day of white-water rafting, celebrating a special occasion, or simply enjoying the tranquility of nature, the sunset cruise offers an unforgettable atmosphere of peace and wonder.
                                </p>
                            </div>
                        </section>

                        {/* Highlights */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                                Cruise Highlights
                            </h2>
                            <div className="grid md:grid-cols-2 gap-8">
                                {[
                                    { icon: 'fa-sun', title: 'Magical Sunsets', description: 'Witness the equatorial sun setting over Lake Victoria, creating perfect photo opportunities.' },
                                    { icon: 'fa-glass-cheers', title: 'Open Bar & Snacks', description: 'Enjoy unlimited local beers, wine, sodas, and water, plus delicious finger foods throughout the cruise.' },
                                    { icon: 'fa-map-location-dot', title: 'Source of the Nile', description: 'Visit the geographic origin of the Nile and see the Speke Monument and Gandhi Statue.' },
                                    { icon: 'fa-binoculars', title: 'Birdwatching', description: 'Spot African fish eagles, kingfishers, cormorants, and other waterbirds in their natural habitat.' },
                                    { icon: 'fa-camera', title: 'Golden Hour Photography', description: 'Capture stunning silhouettes and reflections on the water during the "golden hour."' },
                                    { icon: 'fa-users', title: 'Family Friendly', description: 'A safe and relaxing activity suitable for all ages, from toddlers to seniors.' },
                                    { icon: 'fa-sailboat', title: 'Comfortable Vessels', description: 'Cruise on stable, spacious boats with covered decks and open viewing areas.' },
                                    { icon: 'fa-heart', title: 'Romantic Atmosphere', description: 'The perfect setting for couples, anniversaries, or even a memorable proposal.' }
                                ].map((highlight, index) => (
                                    <div key={index} className="flex gap-4">
                                        <div className="flex-shrink-0">
                                            <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                                <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                                            <p className="text-gray-700">{highlight.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Itinerary */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                                Your Evening Itinerary
                            </h2>
                            <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                                <p className="text-gray-700 text-lg">
                                    <strong>⚠️ Note:</strong> Departure times typically range from 5:00 PM to 5:30 PM depending on the season and operator. We will confirm your exact pickup time upon booking.
                                </p>
                            </div>
                            <div className="space-y-6">
                                {[
                                    { time: '16:45', title: 'Hotel Pickup', desc: 'Your driver picks you up from your Jinja hotel or lodge and transfers you to the riverside dock.' },
                                    { time: '17:00', title: 'Boarding & Welcome Drink', desc: 'Step aboard your vessel. Meet the captain and crew, grab a cold drink from the open bar, and find a comfortable spot on deck.' },
                                    { time: '17:15', title: 'Set Sail to the Source', desc: 'The cruise begins! Head upstream towards the Source of the Nile. Your guide shares stories about John Speke, the river\'s discovery, and local legends.' },
                                    { time: '17:45', title: 'Landmarks & Wildlife', desc: 'Pause at the actual Source point where Lake Victoria spills into the Nile. View the Gandhi statue and Speke monument. Watch for birds and otters along the banks.' },
                                    { time: '18:15', title: 'Golden Hour & Snacks', desc: 'As the sun lowers, enjoy freshly prepared snacks (BBQ skewers, samosas). The lighting becomes magical—perfect for photos.' },
                                    { time: '18:45', title: 'Sunset Spectacle', desc: 'The main event. Watch the sun dip below the horizon, casting a golden glow over the water. Enjoy the serene atmosphere and evening sounds.' },
                                    { time: '19:00', title: 'Return to Dock', desc: 'Cruise back to the mainland under the twilight sky. Disembark and transfer back to your hotel, or head to a riverside restaurant for dinner.' }
                                ].map((step, index) => (
                                    <div key={index} className="card">
                                        <div className="p-6 flex gap-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                                                    {step.time}
                                                </div>
                                            </div>
                                            <div className="flex-grow">
                                                <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                                                <p className="text-gray-700">{step.desc}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* What to Bring */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                                What to Bring
                            </h2>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="card p-6">
                                    <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                                        Clothing & Essentials
                                    </h3>
                                    <ul className="space-y-2 text-gray-700">
                                        <li className="flex items-start gap-2">
                                            <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                                            <span>Light jacket or sweater (it can get breezy after sunset)</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                                            <span>Comfortable casual shoes or sandals</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                                            <span>Sunglasses and hat (for the early part of the cruise)</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                                            <span>Insect repellent (mosquitoes can be active at dusk)</span>
                                        </li>
                                    </ul>
                                </div>
                                <div className="card p-6">
                                    <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon="fa-camera" className="w-6 h-6" />
                                        Gadgets & Extras
                                    </h3>
                                    <ul className="space-y-2 text-gray-700">
                                        <li className="flex items-start gap-2">
                                            <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                                            <span>Camera or smartphone (fully charged)</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                                            <span>Binoculars (great for birdwatching)</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                                            <span>Small cash (for crew tips or souvenirs on land)</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* FAQs */}
                        <FAQSection faqs={faqs} title="Common Questions" />

                        {/* Related Activities */}
                        <section>
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                                More Jinja Adventures
                            </h2>
                            <div className="grid md:grid-cols-2 gap-8">
                                <Link to="/activities/whitewater-rafting" className="card group hover:shadow-xl transition-all duration-300">
                                    <div className="relative h-48 overflow-hidden">
                                        <img
                                            src="https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=800"
                                            alt="Whitewater Rafting"
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Whitewater Rafting</h3>
                                        <p className="text-gray-700 mb-4">Conquer the legendary Grade 5 rapids of the Nile.</p>
                                        <p className="text-safari-gold font-bold">Activity fees apply</p>
                                    </div>
                                </Link>

                                <Link to="/activities/tubing-the-nile" className="card group hover:shadow-xl transition-all duration-300">
                                    <div className="relative h-48 overflow-hidden">
                                        <img
                                            src="https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800"
                                            alt="Tubing the Nile"
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Tubing the Nile</h3>
                                        <p className="text-gray-700 mb-4">Float down the river on a tube with a cold drink in hand.</p>
                                        <p className="text-safari-gold font-bold">Activity fees apply</p>
                                    </div>
                                </Link>
                            </div>
                        </section>
                    </main>

                    {/* Sidebar */}
                    <aside className="lg:col-span-1">
                        <div className="sticky top-24">
                            <div className="card p-6 mb-8">
                                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                                <div className="space-y-4 text-gray-700">
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Duration:</strong> 2 Hours</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-sun" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Time:</strong> 5:00 PM - 7:00 PM</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Price:</strong> $45 - $60 USD</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Age:</strong> All ages welcome</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Availability:</strong> Daily</span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                                >
                                    Book Your Cruise
                                </button>
                            </div>

                            <div className="card p-6 mb-8">
                                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                                    <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                                    What's Included
                                </h3>
                                <ul className="space-y-3">
                                    {[
                                        "2-hour boat cruise",
                                        "Unlimited beer, wine & sodas",
                                        "Light snacks (BBQ/finger food)",
                                        "Visit to Source of the Nile",
                                        "Professional captain & guide",
                                        "Life jackets & safety gear",
                                        "Return hotel transfers (select operators)"
                                    ].map((item, index) => (
                                        <li key={index} className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="card p-6 mb-8">
                                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                                    <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                                    What's Not Included
                                </h3>
                                <ul className="space-y-3">
                                    {[
                                        "Tips for the crew",
                                        "Premium international spirits",
                                        "Personal souvenirs",
                                        "Travel insurance"
                                    ].map((item, index) => (
                                        <li key={index} className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="card p-6">
                                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                                    <FontAwesomeIcon icon="fa-info-circle" className="w-6 h-6" />
                                    Good to Know
                                </h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span><strong>Booking:</strong> Advance booking recommended for weekends.</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span><strong>Groups:</strong> Discounts often available for large groups.</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span><strong>Weather:</strong> Evenings can be cool; bring a light layer.</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            <BookingModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                experience="Jinja Sunset Cruise"
            />
        </div>
    )
}
